import { ReactNode } from 'react';

import { DonutSlice, LegendItem } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { CHART_EXPIRED_COLOR } from 'features/common/utils/chartColors';
import { toSigDigits, toUsFormat } from 'utils/utils';
import {
	formatCompactRevenue,
	formatSummaryFillCenterLabel,
	emptyRing,
	lightenHex,
	expiredShiftHex,
	orderFillLegend,
	paidCount,
	revenueTooltipFormatter,
	COLOR_AVAIL,
	COLOR_OVERSOLD,
	LIGHTEN_FACTOR,
} from './inventoryUtils';
import type { CapacityPool } from 'features/common/hooks/useEventCapacity';
import type { InventoryColumn, InventorySummary } from './useTicketInventoryData';
import type { InventoryColumnCardProps } from './InventoryColumnCard';
import type { ColumnPalette } from './buildTypeColumnProps';

// Summary donut keeps at most this many individual ticket-type slices; the rest
// fold into one neutral "Other" wedge so a many-tier event stays legible. The
// per-type column cards below still show every tier individually.
const TOP_N = 7;

const sum = (cols: InventoryColumn[], fn: (c: InventoryColumn) => number): number =>
	cols.reduce((s, c) => s + fn(c), 0);

export function buildSummaryColumnProps(
	columns: InventoryColumn[],
	summary: InventorySummary,
	palette: ColumnPalette,
	t: (key: string) => string
): InventoryColumnCardProps {
	const { ticketTypeColors, poolColors, onHoldColor, compedColor } = palette;
	const { totalIssued, totalCapacity, effectiveCapacity, totalPayout, summaryCeiling, pools } = summary;
	// Comped aggregate for the issued donut = Σ per-type comped (the same compedCount the paid wedges subtract),
	// NOT the show-level Highlights.tickets_comped: with the paid wedges using per-type comped, the comped slice
	// must too, or the slices (Σ paid + comped) no longer sum to the center (Σ per-type issued). They reconcile
	// by construction whether or not the comped query has loaded.
	const totalComped = sum(columns, (c) => c.compedCount);

	const ttColor = (c: InventoryColumn) => ticketTypeColors[c.name] ?? COLOR_AVAIL;
	const poolAvailColor = (poolName: string) => lightenHex(poolColors[poolName] ?? COLOR_AVAIL, LIGHTEN_FACTOR);

	// ── Top-N fold (issued donut only) ──────────────────────────────────────────

	// col.issued is the inventory snapshot (paid + comped — backend-confirmed). The
	// summary shows a SEPARATE aggregate Comped wedge, so the per-type "sold" wedges
	// must use paid-only (issued − comped); otherwise comps are counted twice in the
	// same ring. (The per-type cards already split soldPaid vs comped identically.)
	const soldPaidOf = (c: InventoryColumn) => paidCount(c.issued, c.compedCount);

	const sortedByIssued = [...columns].sort((a, b) => b.issued - a.issued);
	const named = sortedByIssued.slice(0, TOP_N);
	const folded = sortedByIssued.slice(TOP_N);
	const hasFold = folded.length > 0;
	const otherLabel = `${t('analytics-inventory-other')} (+${folded.length})`;
	const otherAvail = lightenHex(CHART_EXPIRED_COLOR, LIGHTEN_FACTOR);

	const issuedSlices: DonutSlice[] = [
		...named.filter((c) => soldPaidOf(c) > 0).map((c) => ({ label: c.name, value: soldPaidOf(c), color: ttColor(c) })),
		hasFold && sum(folded, soldPaidOf) > 0
			? { label: otherLabel, value: sum(folded, soldPaidOf), color: CHART_EXPIRED_COLOR }
			: null,
	].filter(Boolean) as DonutSlice[];

	// Available is de-double-counted across pools: a standalone type contributes its own pool-capped available,
	// but pooled members do NOT (every member caps to the SAME shared remaining — summing them over-counts).
	// Instead each pool contributes ONE slice = its sellableRemaining (the shared open capacity members can
	// actually draw; slack the pool holds beyond member capacity is excluded — it can never sell). This makes
	// the available slices + sold + comped + holds + expired reconcile with the pool-capped center Y.
	const standaloneNamed = named.filter((c) => !c.poolName);
	const standaloneFolded = folded.filter((c) => !c.poolName);
	const availSlices: DonutSlice[] = [
		...standaloneNamed
			.filter((c) => c.available > 0)
			.map((c) => ({
				label: `${c.name} available`,
				value: c.available,
				color: lightenHex(ttColor(c), LIGHTEN_FACTOR),
			})),
		hasFold && sum(standaloneFolded, (c) => c.available) > 0
			? { label: `${otherLabel} available`, value: sum(standaloneFolded, (c) => c.available), color: otherAvail }
			: null,
		...pools
			.filter((p) => p.sellableRemaining > 0)
			.map((p) => ({
				label: `${p.name} available`,
				value: p.sellableRemaining,
				color: poolAvailColor(p.name),
			})),
	].filter(Boolean) as DonutSlice[];

	// Expired is de-double-counted across pools the same way available is: pooled members' per-member expired
	// shares the pool bucket (showing each would double-count it), so it is folded into ONE per-pool "closed"
	// slice = cappedTotal − sold − sellableRemaining (the bucket capacity no on-sale tier can still sell —
	// e.g. all member tiers past on-sale). Standalone types keep their per-member expired. Together with the
	// pool's sold + sellableRemaining + holds this sums to the pool's effectiveCapacity contribution.
	const poolClosed = (p: Pick<CapacityPool, 'cappedTotal' | 'sold' | 'sellableRemaining'>) =>
		Math.max(0, p.cappedTotal - p.sold - p.sellableRemaining);
	const expiredSlices: DonutSlice[] = [
		...standaloneNamed
			.filter((c) => c.expired > 0)
			.map((c) => ({
				label: `${c.name} expired`,
				value: c.expired,
				color: expiredShiftHex(ttColor(c)),
			})),
		hasFold && sum(standaloneFolded, (c) => c.expired) > 0
			? {
					label: `${otherLabel} expired`,
					value: sum(standaloneFolded, (c) => c.expired),
					color: expiredShiftHex(CHART_EXPIRED_COLOR),
			  }
			: null,
		...pools
			.filter((p) => poolClosed(p) > 0)
			.map((p) => ({
				label: `${p.name} expired`,
				value: poolClosed(p),
				color: expiredShiftHex(poolColors[p.name] ?? COLOR_AVAIL),
			})),
	].filter(Boolean) as DonutSlice[];

	// Genuine reserve (per-type holds) aggregated — NOT pools (pools are their own cards).
	const totalReserve = sum(columns, (c) => c.hold?.amount ?? 0);

	const summaryDonutSlices: DonutSlice[] = [
		...issuedSlices,
		...availSlices,
		totalComped > 0 ? { label: t('analytics-inventory-comped'), value: totalComped, color: compedColor } : null,
		totalReserve > 0 ? { label: t('analytics-inventory-on-hold'), value: totalReserve, color: onHoldColor } : null,
		...expiredSlices,
	].filter(Boolean) as DonutSlice[];

	// Legend colours are projected from the already-built slice arrays (same order) so a predicate change
	// can never drift a slice and its legend dot apart.
	const issuedColors = issuedSlices.map((s) => s.color);
	const availColors = availSlices.map((s) => s.color);
	const expiredColors = expiredSlices.map((s) => s.color);

	const summaryLegendItems: LegendItem[] = [
		{ label: t('analytics-inventory-sold'), colors: issuedColors.length > 0 ? issuedColors : [COLOR_AVAIL] },
		{ label: t('analytics-inventory-available'), colors: availColors.length > 0 ? availColors : [COLOR_AVAIL] },
		totalComped > 0 ? { label: t('analytics-inventory-comped'), color: compedColor } : null,
		totalReserve > 0 ? { label: t('analytics-inventory-on-hold'), color: onHoldColor } : null,
		expiredColors.length > 0 ? { label: 'Expired', colors: expiredColors } : null,
	].filter(Boolean) as LegendItem[];

	// ── Summary fill ring (revenue) ──────────────────────────────────────────────
	// Per-type revenue sub-slices. Not folded (revenue legend is a bounded multi-dot
	// strip); the reserve slice is the genuine hold, not a pool.

	// Comped revenue (given away) across priced types — one aggregate wedge, so the
	// captured (Sold) arc stays paid-only and the ring still sums to the ceiling.
	const summaryCompedRev = columns.reduce(
		(s, c) => (!c.payWhatYouWant && c.initialPrice > 0 ? s + c.compedCount * c.initialPrice : s),
		0
	);

	const summaryFillSlices: DonutSlice[] = (() => {
		const slices: DonutSlice[] = [];
		for (const col of columns) {
			const typeColor = ttColor(col);
			if (!col.payWhatYouWant && col.initialPrice > 0) {
				const price = col.initialPrice;
				const soldRev = paidCount(col.issued, col.compedCount) * price; // paid only
				const holdRev = (col.hold?.amount ?? 0) * price;
				const availRev = col.available * price;
				const expiredRev = col.expired * price;
				if (soldRev > 0) slices.push({ label: `${col.name} sold`, value: soldRev, color: typeColor });
				if (holdRev > 0) slices.push({ label: t('analytics-inventory-on-hold'), value: holdRev, color: onHoldColor });
				if (availRev > 0)
					slices.push({
						label: `${col.name} available`,
						value: availRev,
						color: lightenHex(typeColor, LIGHTEN_FACTOR),
					});
				if (expiredRev > 0)
					slices.push({ label: `${col.name} expired`, value: expiredRev, color: expiredShiftHex(typeColor) });
			} else if (col.payWhatYouWant && col.payout > 0) {
				slices.push({ label: col.name, value: col.payout, color: typeColor });
			}
		}
		if (summaryCompedRev > 0)
			slices.push({ label: t('analytics-inventory-comped'), value: summaryCompedRev, color: compedColor });
		return slices.length > 0 ? slices : [{ label: 'No ceiling', value: 1, color: COLOR_AVAIL }];
	})();

	// Fill legend ordering is centralised in orderFillLegend (single source) — "Available" follows "Sold"
	// (fixes the prior Comped-before-Available order), matching the per-type fill legend.
	const fillIssuedColors = columns.map(ttColor).filter(Boolean) as string[];
	const summaryFillLegendItems: LegendItem[] = orderFillLegend([
		{ key: 'sold', label: 'Sold', colors: fillIssuedColors.length > 0 ? fillIssuedColors : [COLOR_AVAIL] },
		{ key: 'available', label: 'Available', colors: availColors.length > 0 ? availColors : [COLOR_AVAIL] },
		...(summaryCompedRev > 0
			? [{ key: 'comped' as const, label: t('analytics-inventory-comped'), color: compedColor }]
			: []),
		...(totalReserve > 0
			? [{ key: 'onHold' as const, label: t('analytics-inventory-on-hold'), color: onHoldColor }]
			: []),
		...(expiredColors.length > 0 ? [{ key: 'expired' as const, label: 'Expired', colors: expiredColors }] : []),
	]);

	// ── Help buttons ───────────────────────────────────────────────────────────

	const tileHelpButton: ReactNode = (
		<PunchupHelpButton
			mainText="An estimate, not actual revenue: paid tickets × the current unit price for each type. Comped seats are excluded — they capture no revenue. It still ignores fees, add-ons, discounts, and refunds and uses today's price, so it differs from the Revenue tile and the Ticket Sales chart above."
			codeText={
				'total = sum of (paid × unit price) for each type\nPay what you want counted at its max price; free + comped excluded'
			}
			secondaryText="For confirmed payout see the Revenue tile at the top; compare to the fill rings below to see how much of the revenue ceiling has been captured."
		/>
	);

	const issuedHelpButton: ReactNode = (
		<PunchupHelpButton
			mainText="How total capacity is accounted for across all ticket types."
			codeText={
				"Sold = paid tickets (each type's colour)\nComped = complimentary tickets (show total)\nOn Hold = capacity reserved across all tiers\nAvailable = open inventory (lightened type colour)\nExpired = unsold past on-sale window (grey per type)\nOther = the smaller tiers folded together\nCentre = issued (sold + comped) ÷ capacity"
			}
			secondaryText={
				pools.length > 0
					? 'Each tier is shown in full in its own column below. Shared-capacity pools are summarised in their own cards.'
					: 'Each tier is shown in full in its own column below.'
			}
		/>
	);

	const fillHelpButton: ReactNode = (
		<PunchupHelpButton
			mainText="How much of the combined revenue ceiling has been captured across all priced ticket types. Each type appears in its colour, with lightened and muted tones showing uncaptured and expired potential."
			codeText={
				'fill ring = (issued × unit price) per type\ncenter = price-based total (compact)\nPay what you want counted at its max price; free types skipped'
			}
			secondaryText="Lightened segments show uncaptured potential. Grey segments show expired inventory that can no longer be sold."
		/>
	);

	return {
		tile: {
			title: t('analytics-inventory-all-types'),
			value: toSigDigits(totalPayout),
			descriptions: ['Price-based revenue estimate across all ticket types'],
			helpButton: tileHelpButton,
		},
		issuedDonut: {
			// Pool-capped capacity (effectiveCapacity) so the center reconciles with the de-double-counted
			// segments. Equals Σ trueCapacity when the event has no pools. A legacy over-subscribed pool can
			// push totalIssued past effectiveCapacity — the segments then over-fill the ring; we flag it with
			// a red center (matching the per-pool donut) rather than clamp, so the over-capacity state reads
			// honestly. docs/pools_business_logic.md §2.
			centerLabel: `${totalIssued}/${effectiveCapacity}`,
			centerLabelColor: totalIssued > effectiveCapacity ? COLOR_OVERSOLD : undefined,
			centerTooltip: `${totalIssued} issued (sold + comped) of ${effectiveCapacity} sellable capacity`,
			data: summaryDonutSlices.length > 0 ? summaryDonutSlices : emptyRing(),
			legendItems: summaryLegendItems,
			helpButton: issuedHelpButton,
		},
		fillDonut: {
			centerLabel: formatSummaryFillCenterLabel(totalPayout, totalCapacity),
			centerTooltip:
				totalCapacity > 0
					? `${toUsFormat(totalPayout)} of ${formatCompactRevenue(summaryCeiling)} revenue ceiling`
					: undefined,
			data: summaryFillSlices,
			legendItems: summaryFillLegendItems,
			tooltipFormatter: revenueTooltipFormatter,
			helpButton: fillHelpButton,
		},
	};
}
